import { Box, HStack, Icon, Stack, Text } from '@chakra-ui/react'
import React from 'react'
import { Link as ReactRouterLink } from 'react-router-dom'
import { TransferInitiationTransferType } from '../../api/types'
import {
  AccountDetails,
  AccountOwner,
  AccountRestrictionCodeDisplay,
  Correspondent,
} from '../../types'
import {
  Alert,
  Button,
  Section,
  SectionBody,
  SectionHeader,
} from '@apex/component-library'
import { FaArrowLeft, FaExternalLinkAlt } from 'react-icons/fa'
import {
  AccountTransferTable,
  findAndFormatJointOwnerTaxIds,
  JointOwnerTaxIds,
} from './'
import { findTaxIdTypeAndFormatTaxId } from './taxIdUtils'
import { ContextualLink } from '../ContextualLink'
import { RoutesEnum } from '../../types/RoutesEnum'
import CashAssetsPreview from './CashAssets/CashAssetsPreview'
import OptionsPreview from './Options/OptionsPreview'
import { OtherSecuritiesPreview } from './OtherSecurities'
import TransferSubmissionInfo from './TransferSubmissionInfo'
import { TransferInitiation } from '../InitiateTransferPage/types'

export interface AccountTransferPreviewProps {
  accountDetails: AccountDetails
  correspondent: Correspondent
  deliveringAccount: TransferInitiation
  accountOwners: AccountOwner[]
  onEditDetailsClicked: () => void
  onSubmitClicked: () => void
}

interface EditOrSubmitButtonsProps {
  onEditDetailsClicked: () => void
  onSubmitClicked: () => void
}

const EditOrSubmitButtons = ({
  onEditDetailsClicked,
  onSubmitClicked,
}: EditOrSubmitButtonsProps) => (
  <HStack data-testid="edit-or-submit-buttons" spacing="2rem">
    <Button variant="link" color="apex.link" onClick={onEditDetailsClicked}>
      <Icon as={FaArrowLeft} marginRight="4px" />
      Edit Details
    </Button>
    <Button variant="primary" onClick={onSubmitClicked}>
      Confirm Transfer Request
    </Button>
  </HStack>
)

const AccountTransferPreview = ({
  accountDetails: {
    accountNumber: receivingAccountNumber,
    accountTitle: receivingAccountTitle,
    taxId: receivingPrimaryTaxId,
    restrictionCode,
  },
  correspondent: { name: correspondentName, code: correspondentCode },
  deliveringAccount: {
    transferType,
    contraParty: {
      account: deliveringAccountNumber,
      participant: { participantNumber: deliveringParticipantId },
      accountTitle: deliveringAccountTitle,
      primarySsnOrTaxId: deliveringPrimarySsnOrTaxId,
      secondarySsnOrTaxId: deliveringSecondarySsnOrTaxId,
      comment: memo,
    },
    clientReferenceId,
    cash,
    options,
    assets,
  },
  accountOwners,
  onEditDetailsClicked,
  onSubmitClicked,
}: AccountTransferPreviewProps) => {
  const receivingPrimaryTaxIdWithType = findTaxIdTypeAndFormatTaxId(
    accountOwners,
    receivingPrimaryTaxId
  )
  const receivingSecondaryTaxIds = findAndFormatJointOwnerTaxIds(accountOwners)

  return (
    <Stack direction="column" spacing="1.4286rem">
      <EditOrSubmitButtons
        onEditDetailsClicked={onEditDetailsClicked}
        onSubmitClicked={onSubmitClicked}
      />
      {restrictionCode && (
        <Alert
          status="error"
          title="This account is restricted."
          description={AccountRestrictionCodeDisplay[restrictionCode]}
          isClosable={false}
          showIcon
        />
      )}
      <Box data-testid="transfer-request-section">
        <AccountTransferTable
          title="Review Account Transfer Request"
          transferType={
            (transferType === TransferInitiationTransferType.FullTransfer && (
              <Text>
                <span style={{ fontWeight: 600 }}>Full Transfer</span> -
                Transfer all assets from delivering account.
              </Text>
            )) ||
            (transferType ===
              TransferInitiationTransferType.PartialTransferReceiver && (
              <Text>
                <span style={{ fontWeight: 600 }}>Partial Transfer</span> -
                Transfer selected assets from delivering account.
              </Text>
            )) ||
            ''
          }
          deliveringAccountNumber={deliveringAccountNumber}
          receivingAccountNumber={receivingAccountNumber}
          deliveringParticipantId={
            <HStack>
              <Box pr={4}>{deliveringParticipantId}</Box>
              <ContextualLink
                as={ReactRouterLink}
                to={`/${RoutesEnum.ParticipantIds}`}
                icon={FaExternalLinkAlt}
                isExternal
              >
                Common Participant IDs
              </ContextualLink>
            </HStack>
          }
          receivingParticipantId={`${correspondentName.replace(
            `(${correspondentCode})`,
            ''
          )} (0158)`}
          deliveringAccountTitle={deliveringAccountTitle}
          receivingAccountTitle={receivingAccountTitle}
          deliveringPrimaryTaxIdWithType={
            deliveringPrimarySsnOrTaxId
              ? 'No TIN / SSN for Owner'
              : receivingPrimaryTaxIdWithType
          }
          receivingPrimaryTaxIdWithType={receivingPrimaryTaxIdWithType}
          deliveringSecondaryTaxId={
            deliveringSecondarySsnOrTaxId ? (
              'No TIN / SSN for Secondary'
            ) : (
              <JointOwnerTaxIds
                receivingSecondaryTaxIds={receivingSecondaryTaxIds}
              />
            )
          }
          receivingSecondaryTaxIds={receivingSecondaryTaxIds}
          clientReferenceId={clientReferenceId}
          memo={memo}
          showPopoverIcons={false}
          rowHeightOverrideValue="50px"
        />
      </Box>
      {transferType ===
        TransferInitiationTransferType.PartialTransferReceiver && (
        <Section data-testid="assets-section" width="60%">
          <SectionHeader>Assets</SectionHeader>
          <SectionBody>
            <CashAssetsPreview cash={cash} marginTop="1rem" />
            <OptionsPreview options={options} />
            <OtherSecuritiesPreview assets={assets} />
          </SectionBody>
        </Section>
      )}
      <TransferSubmissionInfo />
      <EditOrSubmitButtons
        onEditDetailsClicked={onEditDetailsClicked}
        onSubmitClicked={onSubmitClicked}
      />
    </Stack>
  )
}

export default AccountTransferPreview
